import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Chip,
	Dialog,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import {
	IEditSiteAccessRoleModalProps,
	IEditSiteAccessRoleFormData,
} from 'src/features/site_access/domain/types';
import { EditSiteAccessRoleValidationScheme } from 'src/features/site_access/domain/validation';
import { SITE_ACCESS_LOADING_KEY } from 'src/features/site_access/store';
import { snakeToTitleCase } from 'src/utils/formatter';

export const EditSiteAccessRoleModal: React.FC<
	IEditSiteAccessRoleModalProps
> = ({ open, defaultRoles, defaultType, onClose, onSubmit }) => {
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[SITE_ACCESS_LOADING_KEY],
	);

	const {
		control,
		watch,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<IEditSiteAccessRoleFormData>({
		resolver: yupResolver(EditSiteAccessRoleValidationScheme),
		defaultValues: { roles: defaultRoles, type: defaultType },
	});

	const selectedType = watch('type');

	const handleEditSiteAccessRole = async (
		data: IEditSiteAccessRoleFormData,
	) => {
		if (data.roles == defaultRoles && data.type == defaultType) {
			onClose();
			return;
		}

		onSubmit(data);
	};

	useEffect(() => {
		if (open) {
			reset({ roles: defaultRoles, type: defaultType });
		}
	}, [open, defaultRoles, reset, defaultType]);

	useEffect(() => {
		if (selectedType === 'admin') {
			setValue('roles', []);
		} else {
			setValue('roles', defaultRoles);
		}
	}, [selectedType, reset]);

	if (!open) return null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					borderRadius: 1,
					width: '610px',
					padding: '24px',
				},
			}}
		>
			<form onSubmit={handleSubmit(handleEditSiteAccessRole)}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 3,
						padding: 1,
					}}
				>
					<Typography variant='h6' fontWeight={500}>
						Edit Type and Roles
					</Typography>
					<IconButton
						aria-label='close'
						onClick={onClose}
						sx={{
							position: 'absolute',
							right: 24,
							top: 14,
							color: 'black',
						}}
					>
						<CloseIcon />
					</IconButton>

					<Controller
						name='type'
						control={control}
						render={({ field }) => (
							<FormControl fullWidth error={!!errors?.type}>
								<InputLabel>Type</InputLabel>
								<Select
									{...field}
									label='Type'
									error={!!errors?.type}
								>
									<MenuItem value='admin'>Admin</MenuItem>
									<MenuItem value='member'>Member</MenuItem>
								</Select>
								{errors?.type && (
									<Typography
										color='error'
										variant='caption'
										sx={{
											ml: 1.8,
											fontSize: '0.75rem',
										}}
									>
										{errors.type.message}
									</Typography>
								)}
							</FormControl>
						)}
					/>

					<Controller
						name='roles'
						control={control}
						render={({ field }) => (
							<FormControl
								fullWidth
								error={!!errors?.roles}
								disabled={selectedType === 'admin'}
							>
								<InputLabel>Roles</InputLabel>
								<Select
									label='Roles'
									multiple
									{...field}
									value={field.value || []}
									onChange={(e) =>
										field.onChange(e.target.value)
									}
									renderValue={(selected) => (
										<Box
											sx={{
												display: 'flex',
												flexWrap: 'wrap',
												gap: 0.5,
											}}
										>
											{(selected as string[]).map(
												(value) => (
													<Chip
														key={value}
														label={snakeToTitleCase(
															value,
														)}
														onMouseDown={(
															event,
														) => {
															event.stopPropagation();
														}}
														onDelete={() => {
															const newValue = (
																selected as string[]
															).filter(
																(item) =>
																	item !==
																	value,
															);
															field.onChange(
																newValue,
															);
														}}
													/>
												),
											)}
										</Box>
									)}
								>
									<MenuItem value='data_collector'>
										Data Collector
									</MenuItem>
									<MenuItem value='annotator'>
										Annotator
									</MenuItem>
								</Select>
								{errors?.roles && (
									<Typography
										color='error'
										variant='caption'
										sx={{
											ml: 1.8,
											fontSize: '0.75rem',
										}}
									>
										{errors.roles.message}
									</Typography>
								)}
							</FormControl>
						)}
					/>

					<PrimaryButton type='submit' isLoading={isLoading}>
						SAVE CHANGES
					</PrimaryButton>
				</Box>
			</form>
		</Dialog>
	);
};
